/**
 * Search builder — compose a funnel definition step by step with live counts
 * (docs/crm-search-builder-plan.md §7.3). Left: base-population card + step
 * cards. Right (sticky on xl): the funnel rail + results preview. Every edit
 * debounce-recounts via POST /search/preview; Run materializes a snapshot list.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CalendarClock, Loader2, Play, Save, SearchCheck } from 'lucide-react';
import api from '../../../services/api';
import { PageHeader } from '../../components';
import { fieldCls } from '../../components/filters';
import CriterionPalette from '../../components/search/CriterionPalette';
import StepCard from '../../components/search/StepCard';
import FunnelRail from '../../components/search/FunnelRail';
import StepDrillIn from '../../components/search/StepDrillIn';
import ScheduleModal, { scheduleChipLabel } from '../../components/search/ScheduleModal';
import { LifecycleBadge } from '../../components';
import type {
  CrmForm, SavedSearch, SearchCondition, SearchDefinition, SearchPreviewResponse, SearchStepDef, StudyOption,
} from '../../types';
import { prettySlug, relTime, useTeam } from '../../lib';
import { describeStep, emptyDefinition, newStepId, wireDefinition } from './searchLib';
import { Alert } from '../../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** ?seed= (Field-tag library deep link) — parsed once, then stripped. */
function consumeSeedParam(): SearchDefinition | null {
  const sp = new URLSearchParams(window.location.search);
  const raw = sp.get('seed');
  if (!raw) return null;
  sp.delete('seed');
  const rest = sp.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === 1 && Array.isArray(parsed.steps)) return parsed as SearchDefinition;
  } catch { /* malformed seed — start empty */ }
  return null;
}

export default function SearchBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { team } = useTeam();
  const isNew = !id;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [def, setDef] = useState<SearchDefinition>(() => {
    const seeded = consumeSeedParam();
    const stateSeed = (location.state as any)?.seed as SearchDefinition | undefined;
    return seeded || stateSeed || emptyDefinition();
  });
  const [loadingSearch, setLoadingSearch] = useState(!isNew);
  const [dirty, setDirty] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<SearchPreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [drillStepId, setDrillStepId] = useState<string | null>(null);
  const [meta, setMeta] = useState<SavedSearch | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);

  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [forms, setForms] = useState<CrmForm[]>([]);

  useEffect(() => {
    const stateName = (location.state as any)?.name as string | undefined;
    if (stateName) setName(stateName);
    api.get('/api/crm/studies').then((r) => setStudies(r.data?.studies || [])).catch(() => {});
    api.get('/api/crm/forms').then((r) => {
      const list: CrmForm[] = r.data?.forms || [];
      list.sort((a, b) => (b.counts?.responses || 0) - (a.counts?.responses || 0) || a.title.localeCompare(b.title));
      setForms(list);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the saved search when editing.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoadingSearch(true);
    api.get(`/api/crm/searches/${id}`)
      .then((r) => {
        if (!alive) return;
        const s: SavedSearch = r.data?.search;
        setName(s.name);
        setDescription(s.description || '');
        setDef(s.definition);
        setMeta(s);
        setDirty(false);
      })
      .catch((e) => { if (alive) setError(errMsg(e)); })
      .finally(() => { if (alive) setLoadingSearch(false); });
    return () => { alive = false; };
  }, [id]);

  // ---- Live preview (debounced 500ms on any definition change) ----
  const previewSeq = useRef(0);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runPreview = useCallback(async (definition: SearchDefinition) => {
    const seq = ++previewSeq.current;
    setPreviewing(true);
    try {
      const res = await api.post('/api/crm/search/preview', { definition: wireDefinition(definition) });
      if (seq !== previewSeq.current) return;
      setPreview(res.data || null);
      setError(null);
    } catch (e: any) {
      if (seq === previewSeq.current) setError(errMsg(e));
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  }, []);

  useEffect(() => {
    if (loadingSearch) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => runPreview(def), 500);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [def, loadingSearch, runPreview]);

  const patchDef = (patch: Partial<SearchDefinition>) => {
    setDef((d) => ({ ...d, ...patch }));
    setDirty(true);
  };
  const setStep = (i: number, next: SearchStepDef) =>
    patchDef({ steps: def.steps.map((s, j) => (j === i ? next : s)) });
  const removeStep = (i: number) => patchDef({ steps: def.steps.filter((_, j) => j !== i) });
  const moveStep = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= def.steps.length) return;
    const steps = [...def.steps];
    [steps[i], steps[j]] = [steps[j], steps[i]];
    patchDef({ steps });
  };
  const addStep = (condition: SearchCondition) =>
    patchDef({ steps: [...def.steps, { id: newStepId(), mode: 'include', match: 'any', conditions: [condition] }] });

  // ---- Save / Run ----
  const save = async (): Promise<string | null> => {
    const cleanName = name.trim();
    if (!cleanName) { setError('Give the search a name before saving.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: cleanName, description: description.trim() || null, definition: wireDefinition(def) };
      if (id) {
        await api.patch(`/api/crm/searches/${id}`, body);
        setDirty(false);
        return id;
      }
      const res = await api.post('/api/crm/searches', body);
      const newId = res.data?.search?.id;
      setDirty(false);
      if (newId) navigate(`/crm/search/${newId}`, { replace: true });
      return newId || null;
    } catch (e: any) {
      setError(errMsg(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      let res;
      if (id && !dirty) {
        res = await api.post(`/api/crm/searches/${id}/run`);
      } else if (id) {
        // Persist the edits, then run the saved definition.
        const savedId = await save();
        if (!savedId) { setRunning(false); return; }
        res = await api.post(`/api/crm/searches/${savedId}/run`);
      } else {
        res = await api.post('/api/crm/search/run', { definition: wireDefinition(def) });
      }
      navigate(`/crm/search/runs/${res.data.run_id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setRunning(false);
    }
  };

  const ctx = useMemo(() => ({ studies, team, forms }), [studies, team, forms]);
  const statsByStep = useMemo(() => {
    const m = new Map((preview?.steps || []).map((s) => [s.step_id, s]));
    return (stepId: string) => m.get(stepId) || null;
  }, [preview]);
  const drillStats = drillStepId ? statsByStep(drillStepId) : null;
  const drillStep = def.steps.find((s) => s.id === drillStepId);

  const previewRows = preview?.preview_rows || [];

  if (loadingSearch) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={SearchCheck}
        title={
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            placeholder="Untitled search"
            aria-label="Search name"
            className="w-full max-w-md border-b border-transparent bg-transparent text-lg font-semibold text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400 focus:border-primary-400 dark:text-gray-100"
          />
        }
        subtitle={
          <input
            value={description}
            onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
            placeholder="Description (optional)"
            aria-label="Search description"
            className="w-full max-w-lg border-b border-transparent bg-transparent text-sm text-gray-500 outline-none focus:border-primary-400 dark:text-gray-400"
          />
        }
        actions={
          <>
            {previewing && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Recounting" />}
            <button
              onClick={async () => {
                if (!id) return; // disabled anyway
                if (dirty && !(await save())) return;
                setShowSchedule(true);
              }}
              disabled={!id}
              title={id ? 'Schedule automatic re-runs + notify emails' : 'Save the search first'}
              className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 ${
                meta?.schedule_enabled
                  ? 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100 dark:border-primary-700 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/40'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <CalendarClock className="h-4 w-4" />
              <span className="hidden md:inline">{meta ? scheduleChipLabel(meta) : 'Schedule'}</span>
            </button>
            <button
              onClick={save}
              disabled={saving || (!dirty && Boolean(id))}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {id ? (dirty ? 'Save changes' : 'Saved') : 'Save'}
            </button>
            <button
              onClick={run}
              disabled={running}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Run
            </button>
          </>
        }
      />

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}
      {preview?.budget_exceeded && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          Too broad to preview live — narrow the base population or add a first step, or Run it (runs have a much larger budget).
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,27rem)_minmax(0,1fr)]">
        {/* ---- Left: base + steps ---- */}
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Base population</h2>
              <span className="text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                {preview?.base_total != null ? preview.base_total.toLocaleString() : '—'}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-700 dark:text-gray-300">
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={def.base.exclude_archived !== false}
                  onChange={(e) => patchDef({ base: { ...def.base, exclude_archived: e.target.checked } })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Exclude archived
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={def.base.exclude_test !== false}
                  onChange={(e) => patchDef({ base: { ...def.base, exclude_test: e.target.checked } })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Exclude test leads
              </label>
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                created
                <input
                  type="date"
                  value={def.base.created_from ? String(def.base.created_from).slice(0, 10) : ''}
                  onChange={(e) => patchDef({ base: { ...def.base, created_from: e.target.value || null } })}
                  aria-label="Base created from"
                  className={`${fieldCls} w-auto !py-1 text-xs`}
                />
                to
                <input
                  type="date"
                  value={def.base.created_to ? String(def.base.created_to).slice(0, 10) : ''}
                  onChange={(e) => patchDef({ base: { ...def.base, created_to: e.target.value || null } })}
                  aria-label="Base created to"
                  className={`${fieldCls} w-auto !py-1 text-xs`}
                />
              </span>
            </div>
          </div>

          {def.steps.map((step, i) => (
            <StepCard
              key={step.id}
              step={step}
              stats={statsByStep(step.id)}
              index={i}
              total={def.steps.length}
              onChange={(next) => setStep(i, next)}
              onRemove={() => removeStep(i)}
              onMove={(dir) => moveStep(i, dir)}
              onDrill={() => setDrillStepId(step.id)}
              studies={studies}
              team={team}
              forms={forms}
            />
          ))}

          <CriterionPalette onPick={addStep} label="Add step" />
        </div>

        {/* ---- Right: funnel + results preview (sticky on laptop) ---- */}
        <div className="space-y-3 xl:sticky xl:top-14 xl:self-start">
          <FunnelRail
            baseTotal={preview?.base_total ?? null}
            steps={preview?.steps || []}
            stepDefs={def.steps}
            finalTotal={preview?.final_total ?? null}
            onStepClick={(sid) => setDrillStepId(sid)}
            activeStepId={drillStepId}
            ctx={ctx}
          />
          <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Result preview</h2>
              <span className="text-xs text-gray-400">first {previewRows.length} of {preview?.final_total?.toLocaleString() ?? '—'}</span>
            </div>
            <ul className="max-h-[24rem] divide-y divide-gray-50 overflow-y-auto dark:divide-gray-700/50">
              {previewRows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/crm/contacts/${r.id}`)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100">
                      {r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.phone_e164 || r.email}
                    </span>
                    <LifecycleBadge status={r.lifecycle_status} />
                    {r.effective_study_slug && <span className="hidden text-xs text-gray-400 lg:inline">{prettySlug(r.effective_study_slug)}</span>}
                    {r.created_at && <span className="hidden text-xs text-gray-400 md:inline">{relTime(r.created_at)}</span>}
                  </button>
                </li>
              ))}
              {previewRows.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-gray-400">
                  {preview ? 'No leads match the funnel yet.' : 'Counting…'}
                </li>
              )}
            </ul>
            <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400 dark:border-gray-700">
              Run the search to snapshot the full list — runnable lists power the board's List filter, CSV export, and (soon) study exports.
            </p>
          </div>
        </div>
      </div>

      <StepDrillIn
        open={Boolean(drillStepId)}
        onClose={() => setDrillStepId(null)}
        stepStats={drillStats}
        stepTitle={drillStep ? describeStep(drillStep, ctx) : ''}
        mode="preview"
        samples={drillStats?.samples}
        onOpenLead={(cid) => navigate(`/crm/contacts/${cid}`)}
      />

      {showSchedule && meta && (
        <ScheduleModal
          search={meta}
          team={team}
          onSaved={(updated) => setMeta(updated)}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
}
